// src/modules/calendar/components/GCalImportModal.jsx
// Importar eventos de Google Calendar al sistema vinculados a instructor/curso.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import HxSelect from '../../../components/ui/HxSelect';
import { Users as LUsers, BookOpen as LBook } from 'lucide-react';
import {
    FiX, FiSearch, FiDownload, FiUser, FiBookOpen, FiCheck,
    FiCalendar, FiRefreshCw, FiAlertTriangle, FiCheckSquare, FiSquare,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useGoogleCalendar } from '../../../contexts/GoogleCalendarContext';
import { createSchedule, getSchedules, createCourse } from '../services';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const DAY_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const pad = (n) => String(n).padStart(2, '0');
const fmtTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const fmtDate = (d) => `${d.getDate()} ${['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'][d.getMonth()]} ${d.getFullYear()}, ${DAY_ES[d.getDay()]}`;

// Auto-detectar tipo de evento basado en el título
const detectType = (title) => {
    const t = (title || '').toUpperCase();
    if (/\bSESION\b|\bSESIÓN\b|\bCLASE\b/.test(t)) return 'clase';
    if (/\d+\s*M\b|\bMC\b|\bMASTERCLASS\b|\bMASTER\s*CLASS\b/.test(t)) return 'masterclass';
    return 'masterclass'; // default
};

// ─────────────────────────────────────────────────────────────────────────────
const GCalImportModal = ({ instructors, courses: initialCourses, onClose, onImported }) => {
    const { isConnected, accessToken, syncEvents, googleEvents } = useGoogleCalendar();

    const [loading, setLoading] = useState(false);
    const [allEvents, setAllEvents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [instructorId, setInstructorId] = useState('');
    const [courseId, setCourseId] = useState('');
    const [isImporting, setIsImporting] = useState(false);
    const [existingEvents, setExistingEvents] = useState([]);
    const [courses, setCourses] = useState(initialCourses || []);
    const [isCreatingCourse, setIsCreatingCourse] = useState(false);
    const [newCourseName, setNewCourseName] = useState('');
    const [creatingCourseLoading, setCreatingCourseLoading] = useState(false);

    const handleCreateCourse = async () => {
        const name = newCourseName.trim();
        if (!name) {
            toast.error('Ingresa un nombre para el curso');
            return;
        }
        setCreatingCourseLoading(true);
        try {
            const docRef = await createCourse({ name });
            const newCourse = { id: docRef.id, name };
            setCourses(prev => [...prev, newCourse]);
            setCourseId(docRef.id);
            setIsCreatingCourse(false);
            setNewCourseName('');
            toast.success(`Curso "${name}" creado`);
        } catch (err) {
            toast.error('Error al crear curso: ' + err.message);
        } finally {
            setCreatingCourseLoading(false);
        }
    };

    // Rango configurable — default: 1er día del mes actual → 6 meses adelante
    const now = new Date();
    const defaultStart = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
    const endDate = new Date(now.getFullYear(), now.getMonth() + 6, 0);
    const defaultEnd = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`;
    const [rangeStart, setRangeStart] = useState(defaultStart);
    const [rangeEnd, setRangeEnd] = useState(defaultEnd);

    // Fetch events al montar y cuando cambia el rango
    useEffect(() => {
        if (isConnected) {
            loadGCalEvents();
        }
    }, [isConnected]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cargar eventos existentes del sistema para detectar duplicados
    useEffect(() => {
        getSchedules().then(setExistingEvents).catch(() => setExistingEvents([]));
    }, []);

    const loadGCalEvents = async () => {
        setLoading(true);
        setSelectedIds(new Set());
        try {
            const start = new Date(rangeStart + 'T00:00:00');
            const end = new Date(rangeEnd + 'T23:59:59');
            const evts = await syncEvents(start, end);
            setAllEvents(evts || []);
        } catch {
            toast.error('Error cargando eventos de Google Calendar');
        } finally {
            setLoading(false);
        }
    };

    // Filtrar por búsqueda
    const filtered = useMemo(() => {
        if (!searchTerm.trim()) return allEvents;
        const term = searchTerm.toLowerCase();
        return allEvents.filter(ev => ev.title?.toLowerCase().includes(term));
    }, [allEvents, searchTerm]);

    // Detectar duplicados (mismo título + misma fecha/hora)
    const isDuplicate = useCallback((ev) => {
        return existingEvents.some(ex => {
            const exStart = ex.start instanceof Date ? ex.start : new Date(ex.start);
            const evStart = ev.start instanceof Date ? ev.start : new Date(ev.start);
            return ex.title === ev.title &&
                Math.abs(exStart.getTime() - evStart.getTime()) < 60000; // 1 min tolerance
        });
    }, [existingEvents]);

    // Toggle selección
    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Seleccionar/deseleccionar todos los filtrados
    const toggleAll = () => {
        const filteredIds = filtered.map(ev => ev.id);
        const allSelected = filteredIds.every(id => selectedIds.has(id));
        if (allSelected) {
            setSelectedIds(prev => {
                const next = new Set(prev);
                filteredIds.forEach(id => next.delete(id));
                return next;
            });
        } else {
            setSelectedIds(prev => {
                const next = new Set(prev);
                filteredIds.forEach(id => next.add(id));
                return next;
            });
        }
    };

    // Importar seleccionados
    const handleImport = async () => {
        if (selectedIds.size === 0) {
            toast.error('Selecciona al menos un evento');
            return;
        }
        if (!instructorId) {
            toast.error('Selecciona un instructor');
            return;
        }

        setIsImporting(true);
        let created = 0;
        let skipped = 0;
        const instructor = instructors.find(i => i.id === instructorId);

        try {
            for (const ev of allEvents) {
                if (!selectedIds.has(ev.id)) continue;

                if (isDuplicate(ev)) {
                    skipped++;
                    continue;
                }

                const type = detectType(ev.title);
                await createSchedule({
                    type,
                    title: ev.title,
                    start: ev.start instanceof Date ? ev.start : new Date(ev.start),
                    end: ev.end instanceof Date ? ev.end : new Date(ev.end),
                    color: instructor?.color || '#6366f1',
                    instructorId: instructorId,
                    courseId: courseId || null,
                    status: 'confirmed',
                    importedFromGCal: true,
                    googleCalendarId: ev.googleCalendarId || null,
                });
                created++;
            }

            const msg = skipped > 0
                ? `${created} eventos importados (${skipped} duplicados omitidos)`
                : `${created} eventos importados`;
            toast.success(msg);
            onImported && onImported(created);
            onClose();
        } catch (err) {
            toast.error('Error al importar: ' + err.message);
        } finally {
            setIsImporting(false);
        }
    };

    const selectedCount = selectedIds.size;
    const allFilteredSelected = filtered.length > 0 && filtered.every(ev => selectedIds.has(ev.id));

    if (!isConnected) {
        return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
                    <FiAlertTriangle size={40} className="text-amber-500 mx-auto mb-3" />
                    <h2 className="text-lg font-bold text-gray-800 mb-2">Google Calendar no conectado</h2>
                    <p className="text-sm text-gray-500 mb-4">Conecta tu Google Calendar primero desde la sección de calendario.</p>
                    <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200">Cerrar</button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                            <FiDownload size={18} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-white font-bold text-base">Importar desde Google Calendar</h2>
                            <p className="text-blue-200 text-xs">{allEvents.length} eventos encontrados</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-white/70 hover:text-white hover:bg-white/20 p-1.5 rounded-lg transition">
                        <FiX size={18} />
                    </button>
                </div>

                {/* Config: Instructor + Curso + Buscar */}
                <div className="px-5 py-3 border-b border-gray-100 space-y-3 shrink-0">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                                <FiUser size={11} /> Instructor destino
                            </label>
                            <HxSelect
                                className="full"
                                minWidth={0}
                                value={instructorId}
                                onChange={setInstructorId}
                                options={[...instructors].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(i => ({
                                    value: i.id, label: i.name, sub: i.email || undefined, color: i.color || '#94a3b8', photo: i.photoURL || undefined,
                                }))}
                                placeholder="Selecciona instructor…"
                                searchPlaceholder="Buscar instructor…"
                                icon={LUsers}
                                ariaLabel="Instructor destino"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                                <FiBookOpen size={11} /> Curso (opcional)
                            </label>
                            {isCreatingCourse ? (
                                <div className="flex gap-1.5">
                                    <input
                                        type="text"
                                        value={newCourseName}
                                        onChange={e => setNewCourseName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleCreateCourse()}
                                        placeholder="Nombre del curso..."
                                        autoFocus
                                        className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400 outline-none"
                                    />
                                    <button
                                        onClick={handleCreateCourse}
                                        disabled={creatingCourseLoading}
                                        className="px-2.5 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition disabled:opacity-50"
                                    >
                                        {creatingCourseLoading ? '...' : 'Crear'}
                                    </button>
                                    <button
                                        onClick={() => { setIsCreatingCourse(false); setNewCourseName(''); }}
                                        className="px-2 py-2 text-gray-400 hover:text-gray-600 transition text-sm"
                                    >
                                        <FiX size={16} />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex gap-1.5">
                                    <div style={{ flex: 1, minWidth: 0 }}><HxSelect
                                        className="full"
                                        minWidth={0}
                                        value={courseId}
                                        onChange={setCourseId}
                                        options={[
                                            { value: '', label: 'Todos / Sin asignar', icon: LBook },
                                            ...[...courses].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => ({ value: c.id, label: c.name, sub: c.abreviatura || undefined })),
                                        ]}
                                        placeholder="Todos / Sin asignar"
                                        searchPlaceholder="Buscar curso…"
                                        icon={LBook}
                                        ariaLabel="Curso"
                                    /></div>
                                    <button
                                        onClick={() => setIsCreatingCourse(true)}
                                        title="Crear nuevo curso"
                                        className="px-2.5 py-2 bg-green-50 text-green-600 border border-green-200 rounded-lg text-xs font-semibold hover:bg-green-100 transition whitespace-nowrap"
                                    >
                                        + Nuevo
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Rango de fechas */}
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                        <div>
                            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Desde</label>
                            <input
                                type="date"
                                value={rangeStart}
                                onChange={e => setRangeStart(e.target.value)}
                                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Hasta</label>
                            <input
                                type="date"
                                value={rangeEnd}
                                onChange={e => setRangeEnd(e.target.value)}
                                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none"
                            />
                        </div>
                        <button
                            onClick={loadGCalEvents}
                            disabled={loading}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                        >
                            <FiRefreshCw size={12} className={loading ? 'animate-spin' : ''} />
                            Cargar
                        </button>
                    </div>

                    {/* Búsqueda */}
                    <div className="relative">
                        <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Filtrar por nombre... ej: INDEPENDIZACION"
                            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 outline-none"
                        />
                    </div>
                    {searchTerm && (
                        <p className="text-xs text-blue-600">
                            {filtered.length} de {allEvents.length} evento(s) para "{searchTerm}"
                        </p>
                    )}
                </div>

                {/* Lista de eventos */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <span className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            <FiCalendar size={32} className="mx-auto mb-2" />
                            <p className="text-sm">{searchTerm ? 'No se encontraron eventos' : 'No hay eventos en Google Calendar'}</p>
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
                                <tr>
                                    <th className="px-3 py-2 w-10">
                                        <button onClick={toggleAll} className="text-gray-400 hover:text-blue-600 transition">
                                            {allFilteredSelected ? <FiCheckSquare size={16} /> : <FiSquare size={16} />}
                                        </button>
                                    </th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase">Fecha</th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase">Hora</th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase">Tipo</th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase">Evento</th>
                                    <th className="px-2 py-2 w-16 text-[10px] font-semibold text-gray-400 uppercase">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((ev, i) => {
                                    const selected = selectedIds.has(ev.id);
                                    const dup = isDuplicate(ev);
                                    const type = detectType(ev.title);
                                    const isMC = type === 'masterclass';
                                    const start = ev.start instanceof Date ? ev.start : new Date(ev.start);
                                    const end = ev.end instanceof Date ? ev.end : new Date(ev.end);

                                    return (
                                        <tr
                                            key={ev.id}
                                            onClick={() => !dup && toggleSelect(ev.id)}
                                            className={`border-b border-gray-50 cursor-pointer transition-colors ${
                                                dup ? 'opacity-40 cursor-not-allowed' :
                                                selected ? 'bg-blue-50 hover:bg-blue-100' :
                                                i % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/40 hover:bg-gray-100/60'
                                            }`}
                                        >
                                            <td className="px-3 py-2 text-center">
                                                {dup ? (
                                                    <span className="text-gray-300" title="Ya importado">
                                                        <FiCheck size={14} />
                                                    </span>
                                                ) : selected ? (
                                                    <FiCheckSquare size={16} className="text-blue-600" />
                                                ) : (
                                                    <FiSquare size={16} className="text-gray-300" />
                                                )}
                                            </td>
                                            <td className="px-3 py-2">
                                                <p className="text-xs font-medium text-gray-800">{fmtDate(start)}</p>
                                            </td>
                                            <td className="px-3 py-2">
                                                <p className="text-xs text-gray-600">{fmtTime(start)} – {fmtTime(end)}</p>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                                    isMC ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                    {isMC ? '★ MC' : '● Clase'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2">
                                                <p className="text-xs font-semibold text-gray-800 truncate max-w-xs">{ev.title}</p>
                                            </td>
                                            <td className="px-2 py-2 text-center">
                                                {dup ? (
                                                    <span className="text-[10px] text-gray-400 font-medium">Importado</span>
                                                ) : (
                                                    <span className="text-[10px] text-gray-300">Nuevo</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/60 flex items-center justify-between shrink-0">
                    <p className="text-xs text-gray-500">
                        {selectedCount > 0
                            ? `${selectedCount} evento(s) seleccionados`
                            : 'Selecciona eventos para importar'
                        }
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 text-sm font-medium hover:bg-gray-100 transition"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleImport}
                            disabled={isImporting || selectedCount === 0 || !instructorId}
                            className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-200 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            {isImporting ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Importando...
                                </>
                            ) : (
                                <>
                                    <FiDownload size={15} />
                                    Importar {selectedCount} eventos
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GCalImportModal;
